import { useReso } from '../contexts/ResoContext';
import {
  BaseResourceConfig,
  getResourceConfig,
  isListingResourceConfig,
  ListingResourceConfig,
  ResoResourceBase,
  ResoResourceManager,
  ResourceManagerOptions,
} from './types';
import { FieldTranslator } from './FieldTranslator';
import { TaskManager } from './TaskManager';
import {
  addCwaLisKey,
  addCwaPrefix,
  addCwaPrefixToFieldName,
  buildODataFilter,
  executeWithPagination,
  executeWithRetry,
  getEntityName,
  getItemNumberField,
  getKeyField,
  getLisKeyField,
  getListingKeyField,
  getTimestampField,
  removeCwaPrefix,
} from './utils';
import { ListingResource } from '../system/field';
import { useListingStore } from '@brightmls/nextgen.lae.flc';

export abstract class BaseResourceManager<
  T extends ResoResourceBase,
> implements ResoResourceManager<T> {
  public taskManager: TaskManager;

  protected resourceType: ListingResource | string;
  protected executeQuery: ReturnType<typeof useReso>['executeQuery'];
  protected config: BaseResourceConfig;
  protected fieldTranslator: FieldTranslator;
  protected options: ResourceManagerOptions;
  protected lastContextSnapshot: any = null;

  private _activeTasks: Set<string> = new Set();
  private _maxRetries: number = 3;

  constructor(
    resourceType: ListingResource | string,
    executeQuery: ReturnType<typeof useReso>['executeQuery'],
    fieldTranslator: FieldTranslator,
    taskManager: TaskManager,
    options: ResourceManagerOptions = {},
  ) {
    this.resourceType = resourceType;
    this.executeQuery = executeQuery;
    this.config = getResourceConfig(resourceType);
    this.fieldTranslator = fieldTranslator;
    this.taskManager = taskManager;
    this.options = {
      enableCwa: false,
      useTranslation: true,
      autoPage: undefined,
      ...options,
    };
  }

  /**
   * Core mapping function from API data to model object
   * This is implemented by subclasses
   */
  protected abstract mapFromApiCore(apiData: any): T;

  /**
   * Core mapping function from model object to API data
   * This is implemented by subclasses
   */
  protected abstract mapToApiCore(resource: T): any;

  /**
   * Map from API to model with translation if needed
   */
  protected mapFromApi(apiData: any): T {
    let processedData = apiData;

    // Remove CWA prefix if data comes from CWA
    if (
      this.options.enableCwa ||
      Object.keys(apiData).some((key) => key.startsWith('CWA'))
    ) {
      processedData = removeCwaPrefix(apiData);
    }

    // Apply field translation if enabled
    const translatedData = this.options.useTranslation
      ? this.fieldTranslator.translateToApp(processedData)
      : processedData;

    // Apply resource-specific mapping
    return this.mapFromApiCore(translatedData);
  }

  /**
   * Map from model to API with translation if needed
   */
  protected mapToApi(resource: T): any {
    // Apply resource-specific mapping
    const apiData = this.mapToApiCore(resource);

    // Apply field translation if enabled
    const translatedData = this.options.useTranslation
      ? this.fieldTranslator.translateToReso(apiData)
      : apiData;

    // Add CWA prefix if needed
    return this.options.enableCwa && this.config.supportsCwa
      ? addCwaPrefix(translatedData)
      : translatedData;
  }

  /**
   * Direct FLC store access - no hooks needed!
   * This is the key to using FLC in non-component code
   */
  protected get store() {
    return useListingStore.getState();
  }

  /**
   * Get listing key from FLC store
   */
  protected getContextListingKey(): string | undefined {
    // Direct field access - surgical precision!
    return this.store.Property?.LISTING_KEY || undefined;
  }

  /**
   * Get listing edit key from FLC store
   */
  protected getContextEditKey(): string | undefined {
    // Direct field access - no re-renders!
    return this.store.Property?.EDIT_KEY || undefined;
  }

  /**
   * Take a snapshot of the current context resources
   * Used for rollback in case of operation failure
   */
  protected takeContextSnapshot(): void {
    if (this.hasListingContext()) {
      const currentResource = this.store[this.resourceType as ListingResource];
      // Deep clone to ensure we have a proper snapshot
      this.lastContextSnapshot = Array.isArray(currentResource)
        ? [...currentResource]
        : { ...currentResource };
    }
  }

  /**
   * Restore the context from snapshot
   * Used for rollback in case of operation failure
   */
  protected restoreContextFromSnapshot(): void {
    if (this.hasListingContext() && this.lastContextSnapshot) {
      // Use FLC's setResource for atomic updates
      this.store.setResource(
        this.resourceType as ListingResource,
        this.lastContextSnapshot,
      );
    }
  }

  /**
   * Check if we have listing context data
   */
  protected hasListingContext(): boolean {
    return (
      this.config.hasListingContext &&
      this.store[this.resourceType as ListingResource] !== undefined
    );
  }

  /**
   * Create a new instance with CWA mode enabled
   * Only available for resources that support CWA
   */
  public cwa(): ResoResourceManager<T> {
    // Only allow CWA mode for resources that support it
    if (!this.config.supportsCwa) {
      throw new Error(
        `Resource type ${this.resourceType} doesn't support CWA mode`,
      );
    }

    const CwaResourceManagerClass = this.constructor as new (
      resourceType: ListingResource | string,
      executeQuery: ReturnType<typeof useReso>['executeQuery'],
      fieldTranslator: FieldTranslator,
      taskManager: TaskManager,
      options?: ResourceManagerOptions,
    ) => ResoResourceManager<T>;

    // Create the new instance
    const newManager = new CwaResourceManagerClass(
      this.resourceType,
      this.executeQuery,
      this.fieldTranslator,
      this.taskManager,
      {
        ...this.options,
        enableCwa: true,
      },
    );

    // Ensure taskManager methods are properly bound
    if (newManager.taskManager && !newManager.taskManager.trackTask) {
      newManager.taskManager = this.taskManager;
    }

    return newManager;
  }

  /**
   * Check if a specific task is in progress
   */
  public isTaskInProgress(taskId?: string): boolean {
    if (taskId) {
      return this._activeTasks.has(taskId);
    }

    // Check if any task for this resource is in progress
    return this.taskManager.isResourceBusy(this.resourceType);
  }

  /**
   * Check if a specific resource is being updated
   */
  public isUpdatingItem(resourceId: string | number): boolean {
    return this.taskManager.isResourceIdBusy(this.resourceType, resourceId);
  }

  /**
   * Check if any task is in progress
   */
  public isAnyTaskInProgress(): boolean {
    return this.taskManager.isAnyTaskInProgress();
  }

  /**
   * Get all resources of this type for a listing
   */
  public async list(listingKey?: string): Promise<T[]> {
    return this.taskManager.trackTask(
      this.resourceType,
      'list',
      { taskName: 'list' },
      async (taskId) => {
        this._activeTasks.add(taskId);

        try {
          // Check FLC store cache first
          if (!listingKey && this.hasListingContext()) {
            const contextResources =
              this.store[this.resourceType as ListingResource];
            if (
              Array.isArray(contextResources) &&
              contextResources.length > 0
            ) {
              return contextResources.map((resource) =>
                this.mapFromApi(resource),
              );
            }

            // Get listing key from store if not provided
            listingKey = this.getContextListingKey();
          }

          // For non-listing resources without a listingKey, just return an empty array
          if (!listingKey && !isListingResourceConfig(this.config)) {
            return [];
          }

          // For listing resources, we need a listing key to filter by
          if (!listingKey && isListingResourceConfig(this.config)) {
            return [];
          }

          let queryParams: Record<string, any> = {
            $format: 'json',
          };

          // Add listing key filter for listing resources
          if (isListingResourceConfig(this.config) && listingKey) {
            const listingKeyField = getListingKeyField(
              this.config,
              this.options.enableCwa,
            );
            if (listingKeyField) {
              queryParams.$filter = `${listingKeyField} eq '${listingKey}'`;
            }
          }

          // Execute with retry
          const response: any = await executeWithRetry(
            async () => {
              // Use pagination utility if autoPage is set
              if (this.options.autoPage) {
                return executeWithPagination(
                  this.executeQuery,
                  getEntityName(this.config, this.options.enableCwa),
                  { queryParams },
                  this.options.autoPage,
                );
              } else {
                return this.executeQuery(
                  getEntityName(this.config, this.options.enableCwa),
                  { queryParams },
                );
              }
            },
            {
              maxRetries: this._maxRetries,
            },
          );

          const resources = (response as any).value || [];

          // Sync with FLC store if needed
          if (this.hasListingContext()) {
            this.store.setResource(
              this.resourceType as ListingResource,
              resources,
            );
          }

          return resources.map((resource: any) => this.mapFromApi(resource));
        } finally {
          this._activeTasks.delete(taskId);
        }
      },
    );
  }

  public getEntityName() {
    return getEntityName(this.config, this.options.enableCwa);
  }

  /**
   * Get a single resource by its ID
   */
  public async getById(id: string | number): Promise<T | null> {
    return this.taskManager.trackTask(
      this.resourceType,
      'other',
      { taskName: 'getById', resourceId: id },
      async (taskId) => {
        this._activeTasks.add(taskId);

        try {
          const entityName = getEntityName(this.config, this.options.enableCwa);
          const response = await executeWithRetry(
            async () => {
              return this.executeQuery(`${entityName}('${id}')`, {
                queryParams: {
                  $format: 'json',
                },
              });
            },
            {
              maxRetries: this._maxRetries,
            },
          );

          if (!response) {
            return null;
          }

          return this.mapFromApi(response);
        } catch (error) {
          console.error(`Failed to get ${this.resourceType} by ID:`, error);
          return null;
        } finally {
          this._activeTasks.delete(taskId);
        }
      },
    );
  }

  public updateExecuteQuery(
    executeQuery: ReturnType<typeof useReso>['executeQuery'],
  ): void {
    this.executeQuery = executeQuery;
  }

  /**
   * Find resource by predicate
   */
  public async find(predicate: (resource: T) => boolean): Promise<T | null> {
    const resources = await this.list();
    return resources.find(predicate) || null;
  }

  /**
   * Find resource by field value (returns the first match)
   * @param field Field name
   * @param value Field value
   * @returns First matching resource or null
   */
  public async findByField(field: string, value: any): Promise<T | null> {
    const results = await this.findListByField(field, value);
    return results.length > 0 ? results[0] : null;
  }

  /**
   * Find resources by field value (returns all matches)
   * @param field Field name
   * @param value Field value
   * @returns Array of matching resources
   */
  public async findListByField(field: string, value: any): Promise<T[]> {
    return this.findListByFields({ [field]: value });
  }

  /**
   * Find resource by multiple field values (returns the first match)
   * @param fields Object with field name/value pairs
   * @returns First matching resource or null
   */
  public async findByFields(fields: Record<string, any>): Promise<T | null> {
    const results = await this.findListByFields(fields);
    return results.length > 0 ? results[0] : null;
  }

  /**
   * Find resources by multiple field values (returns all matches)
   * @param fields Object with field name/value pairs
   * @returns Array of matching resources
   */
  public async findListByFields(fields: Record<string, any>): Promise<T[]> {
    try {
      // Convert field names to their API format if needed
      const apiFields: Record<string, any> = {};

      for (const [field, value] of Object.entries(fields)) {
        const apiField = this.options.useTranslation
          ? this.fieldTranslator.toResoField(field)
          : field;

        const prefixedField =
          this.options.enableCwa && this.config.supportsCwa
            ? addCwaPrefixToFieldName(apiField)
            : apiField;

        apiFields[prefixedField] = value;
      }

      // Build OData filter
      const filter = buildODataFilter(apiFields);

      // Execute query
      const response: any = await executeWithRetry(
        async () => {
          const queryOptions = {
            queryParams: {
              $filter: filter,
              $format: 'json',
            },
          };

          if (this.options.autoPage) {
            return executeWithPagination(
              this.executeQuery,
              getEntityName(this.config, this.options.enableCwa),
              queryOptions,
              this.options.autoPage,
            );
          } else {
            return this.executeQuery(
              getEntityName(this.config, this.options.enableCwa),
              queryOptions,
            );
          }
        },
        {
          maxRetries: this._maxRetries,
        },
      );

      if (
        !response?.value ||
        !Array.isArray(response.value) ||
        response.value.length === 0
      ) {
        return [];
      }

      // Map all results
      return response.value.map((item: any) => this.mapFromApi(item));
    } catch (error) {
      console.error(`Failed to find ${this.resourceType} by fields:`, error);
      return [];
    }
  }

  /**
   * Create a new resource
   */
  public async create(resource: T): Promise<T> {
    return this.taskManager.trackTask(
      this.resourceType,
      'create',
      { resource },
      async (taskId) => {
        this._activeTasks.add(taskId);

        try {
          // Take snapshot for potential rollback
          this.takeContextSnapshot();

          // Add timestamp if applicable
          const timestampField = getTimestampField(
            this.config as ListingResourceConfig,
          );
          let resourceWithTimestamp = resource as any;

          if (timestampField) {
            resourceWithTimestamp = {
              ...resourceWithTimestamp,
              [timestampField]: new Date().toISOString(),
            };
          }

          // Add LIS key if in CWA mode
          if (isListingResourceConfig(this.config) && this.options.enableCwa) {
            const lisKey = this.getContextEditKey();
            if (lisKey && isListingResourceConfig(this.config)) {
              resourceWithTimestamp = addCwaLisKey(
                resourceWithTimestamp as Record<string, any>,
                this.config,
                lisKey,
              ) as T;
            }
          }

          // Map resource to API format
          const apiResource = this.mapToApi(resourceWithTimestamp);

          // Optimistic update using FLC patterns
          if (this.hasListingContext()) {
            const currentResources = [
              ...(this.store[this.resourceType as ListingResource] as any[]),
            ];
            currentResources.push(apiResource);

            // Atomic update through store
            this.store.setResource(
              this.resourceType as ListingResource,
              currentResources,
            );
          }

          // Create resource via API with retry
          const response = await executeWithRetry(
            async () => {
              return this.executeQuery(
                getEntityName(this.config, this.options.enableCwa),
                {
                  method: 'POST',
                  body: apiResource,
                },
              );
            },
            {
              maxRetries: this._maxRetries,
            },
          );

          // Update store with actual response
          if (this.hasListingContext()) {
            const currentResources = this.store[
              this.resourceType as ListingResource
            ] as any[];
            const updatedResources = currentResources.map((r) =>
              // Replace optimistic version with real response
              r === apiResource ? response : r,
            );

            this.store.setResource(
              this.resourceType as ListingResource,
              updatedResources,
            );
          }

          return this.mapFromApi(response);
        } catch (error) {
          console.error(`Failed to create ${this.resourceType}:`, error);

          // Restore context on failure
          this.restoreContextFromSnapshot();

          throw error;
        } finally {
          this._activeTasks.delete(taskId);
        }
      },
    );
  }

  /**
   * Optimistic update to listing context using FLC patterns
   */
  protected updateContextOptimistically(
    resourceKey: string | number,
    contextKeyName: string,
    contextResource: Partial<any>,
  ): void {
    if (this.hasListingContext()) {
      // Use FLC's surgical update method
      this.store.updateResourceByKey(
        this.resourceType as ListingResource,
        resourceKey,
        contextKeyName,
        contextResource,
      );
    }
  }

  /**
   * Update an existing resource - properly using resource configuration
   */
  public async update(resource: T): Promise<T> {
    if (!isListingResourceConfig(this.config)) {
      throw new Error('This resource cannot be saved');
    }

    const config = this.config as ListingResourceConfig;

    return this.taskManager.trackTask(
      this.resourceType,
      'update',
      { resource },
      async (taskId) => {
        this._activeTasks.add(taskId);

        try {
          // Take snapshot for potential rollback
          this.takeContextSnapshot();

          let resourceKey = resource.getKey();
          if (!resourceKey) {
            // Try to find by item number if no key is provided
            const itemNumber = resource.getItemNumber();
            const listingKey =
              resource.getListingKey() || this.getContextListingKey();

            if (itemNumber && config.itemNumberField && listingKey) {
              const existingResource = await this.findByFields({
                [config.itemNumberField]: itemNumber,
                [config.listingKeyField]: listingKey,
              });

              if (existingResource) {
                resourceKey = existingResource.getKey();
              }
            }

            if (!resourceKey) {
              throw new Error(
                `Cannot update ${this.resourceType} without a key or item number`,
              );
            }
          }

          // Add timestamp if applicable
          const timestampField = getTimestampField(config);
          let resourceWithTimestamp = resource as any;

          if (timestampField) {
            resourceWithTimestamp = {
              ...resourceWithTimestamp,
              [timestampField]: new Date().toISOString(),
            };
          }

          // Add LIS key if in CWA mode
          const lisKeyField = getLisKeyField(config, this.options.enableCwa);
          if (this.options.enableCwa && lisKeyField) {
            const lisKey = this.getContextEditKey();
            if (lisKey) {
              resourceWithTimestamp = {
                ...resourceWithTimestamp,
                [lisKeyField]: lisKey,
              };
            }
          }

          // Map resource to API format
          const apiResource = this.mapToApi(resourceWithTimestamp);

          // The key field for context is translated by fieldTranslator
          const contextKeyField = this.fieldTranslator.translateToApp({
            [config.keyField]: null,
          });
          const contextKeyName = Object.keys(contextKeyField)[0];

          // Optimistic update using FLC's surgical update
          if (this.hasListingContext()) {
            const contextResource = this.mapFromApi(apiResource);
            this.updateContextOptimistically(
              resourceKey,
              contextKeyName,
              contextResource as Partial<any>,
            );
          }

          // Update resource via API with retry
          const response = await executeWithRetry(
            async () => {
              return this.executeQuery(
                `${this.getEntityName()}('${resourceKey}')`,
                {
                  method: 'PATCH',
                  body: apiResource,
                },
              );
            },
            {
              maxRetries: this._maxRetries,
            },
          );

          // Map the API response back to model format
          const responseViewModel = this.mapFromApi(response);

          // Ensure context is updated with API response data
          if (this.hasListingContext()) {
            this.updateContextOptimistically(
              resourceKey,
              contextKeyName,
              responseViewModel as Partial<any>,
            );
          }

          return responseViewModel;
        } catch (error) {
          console.error(`Failed to update ${this.resourceType}:`, error);

          // Restore context on failure
          this.restoreContextFromSnapshot();

          throw error;
        } finally {
          this._activeTasks.delete(taskId);
        }
      },
    );
  }

  /**
   * Save a resource (create or update based on whether it has a key or can be found by item number)
   */
  public async save(resource: T): Promise<T> {
    const key = resource.getKey();
    if (!isListingResourceConfig(this.config)) {
      throw new Error('This resource can not be saved');
    }

    if (key) {
      // If we have a key, use update
      return this.update(resource);
    } else {
      const itemNumber = resource.getItemNumber();
      const listingKey =
        resource.getListingKey() || this.getContextListingKey();

      // If it has an item number but no key, check if it exists already
      if (itemNumber && this.config.itemNumberField && listingKey) {
        try {
          // Find by item number and listing key
          const existingResource = await this.findByFields({
            [this.config.itemNumberField]: itemNumber,
            [this.config.listingKeyField]: listingKey,
          });

          if (existingResource) {
            // Found existing resource, merge with current data and update it
            const mergedResource = {
              ...existingResource,
              ...(resource as any),
              // Ensure we keep the key
              [this.config.keyField]: existingResource.getKey(),
            } as T;

            return this.update(mergedResource);
          }
        } catch (error) {
          console.error(
            `Error checking for existing resource by item number:`,
            error,
          );
        }
      }

      // Create new if no key or not found
      return this.create(resource);
    }
  }

  /**
   * Delete a resource by resource object, key, or item number
   */
  public async delete(resource: T | string): Promise<boolean> {
    const config = this.config as ListingResourceConfig;

    return this.taskManager.trackTask(
      this.resourceType,
      'delete',
      { resource },
      async (taskId) => {
        this._activeTasks.add(taskId);

        try {
          // Take snapshot for potential rollback
          this.takeContextSnapshot();

          let resourceKey =
            typeof resource === 'string' ? resource : resource.getKey();
          let itemNumber: number | undefined;
          let listingKey: string | undefined;

          if (typeof resource !== 'string') {
            itemNumber = resource.getItemNumber();
            listingKey =
              resource.getListingKey() || this.getContextListingKey();

            if (
              !resourceKey &&
              itemNumber &&
              config.itemNumberField &&
              listingKey
            ) {
              const existingResource = await this.findByFields({
                [config.itemNumberField]: itemNumber,
                [config.listingKeyField]: listingKey,
              });

              if (existingResource) {
                resourceKey = existingResource.getKey();
              } else {
                // Resource not found - consider this a successful deletion
                return true;
              }
            }
          }

          if (!resourceKey) {
            throw new Error(
              `Cannot delete ${this.resourceType} without a key or item number`,
            );
          }

          // Get the key field for current mode
          const keyField = getKeyField(config, this.options.enableCwa);

          // Optimistic update - remove from FLC store
          if (this.hasListingContext()) {
            const currentResources = this.store[
              this.resourceType as ListingResource
            ] as any[];
            const predicate = (item: any) => {
              if (item[keyField] === resourceKey) {
                return true;
              }

              if (config.itemKey && item[config.itemKey] === resourceKey) {
                return true;
              }

              // Also match by item number and listing key if available
              if (
                itemNumber &&
                config.itemNumberField &&
                listingKey &&
                item[getItemNumberField(config, this.options.enableCwa)!] ===
                  itemNumber &&
                item[getListingKeyField(config, this.options.enableCwa)!] ===
                  listingKey
              ) {
                return true;
              }

              return false;
            };

            const updatedResources = currentResources.filter(
              (item) => !predicate(item),
            );

            // Atomic update through store
            this.store.setResource(
              this.resourceType as ListingResource,
              updatedResources,
            );
          }

          // Delete resource via API with retry
          await executeWithRetry(
            async () => {
              return this.executeQuery(
                `${this.getEntityName()}('${resourceKey}')`,
                {
                  method: 'DELETE',
                },
              );
            },
            {
              maxRetries: this._maxRetries,
            },
          );

          return true;
        } catch (error) {
          console.error(`Failed to delete ${this.resourceType}:`, error);

          // Restore context on failure
          this.restoreContextFromSnapshot();

          return false;
        } finally {
          this._activeTasks.delete(taskId);
        }
      },
    );
  }
}
